import { useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { AlertCircleIcon, SearchIcon, XIcon } from 'lucide-react'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { DrawerHeaderLayout, SelectionBar } from '~/components/drawer-layout'
import * as EmptyState from '~/components/EmptyState'
import { Image } from '~/components/Media/Image'
import { Video } from '~/components/Media/Video'
import { MediaCard } from '~/components/media-card'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { useUnsavedChangesWarning } from '~/hooks/use-unsaved-changes-warning'
import type { ProductFull } from '~/models/product.server'
import { Drawer, IconButton, Input, Spinner, Tooltip } from '~/ui'
import { cn } from '~/utils/classnames'
import { getProductCoverMedia } from '~/utils/product'

import { useCollectionConfiguration } from './collection-configuration-context'
import { CollectionProductCard } from './collection-product-card'
import type { CollectionFormData, CollectionItemFormData } from './type'

// Types
type CollectionItemsDrawerProps = {
  existingItems?: CollectionItemFormData[]
  collection: CollectionFormData
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose: () => void
}

// type CollectionItemToUpdate = {
//   id?: string
//   collectionId?: string
//   productId?: string
//   disposition?: string
//   product: SerializeFrom<ProductFull>
// }

// Custom hooks
const useProductSearch = (
  products: SerializeFrom<ProductFull>[],
  searchValue: string
) => {
  return useMemo(() => {
    return products.filter((_prod) =>
      _prod.name.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [products, searchValue])
}

const useSelectedProducts = (itemsToUpdate: CollectionItemFormData[]) => {
  const selectedProducts = useMemo(() => {
    return itemsToUpdate?.map((item) => item.product) || []
  }, [itemsToUpdate])

  const selectedProductsIds = useMemo(() => {
    return selectedProducts?.map((prod) => prod.id) || []
  }, [selectedProducts])

  return { selectedProducts, selectedProductsIds }
}

const ProductGrid = ({
  products,
  selectedProductsIds,
  onProductToggle,
}: {
  products: SerializeFrom<ProductFull>[]
  selectedProductsIds: string[]
  onProductToggle: (
    product: SerializeFrom<ProductFull>,
    checked: boolean
  ) => void
}) => {
  const { t } = useTranslation()

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-8 py-12">
        <EmptyState.Product />
        <h2 className="text-foreground/64 mt-4 text-sm font-medium">
          No products found
        </h2>
      </div>
    )
  }

  return (
    <div className="grid w-full grid-cols-2 gap-4 px-4 pb-56 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {products.map((product) => {
        const isSelected = selectedProductsIds.includes(product.id)
        return (
          <label key={product.id} className="cursor-pointer">
            <input
              checked={isSelected}
              type="checkbox"
              className="hidden"
              onChange={(event) =>
                onProductToggle(product, event.target.checked)
              }
            />
            <CollectionProductCard
              product={product}
              active={isSelected}
              aspectRatio="square"
            />
          </label>
        )
      })}
    </div>
  )
}

const SelectedProductsBar = ({
  selectedProducts,
  onReorder,
  onProductRemove,
  onSave,
  error,
}: {
  selectedProducts: SerializeFrom<ProductFull>[]
  onReorder: (newOrder: string[]) => void
  onProductRemove: (productId: string) => void
  onSave: () => void
  error: boolean
}) => {
  const { t } = useTranslation()

  return (
    <SelectionBar>
      <SelectionBar.Actions>
        <SelectionBar.DragHint />

        <div className="xs:w-auto flex w-full items-center gap-3">
          {error && (
            <Tooltip delayIn={100} placement="top-start">
              <Tooltip.Trigger>
                <AlertCircleIcon className="text-destructive" />
              </Tooltip.Trigger>
              <Tooltip.Content>
                {t(
                  'page.collections.save_collection_items.error.update_failed'
                )}
              </Tooltip.Content>
            </Tooltip>
          )}

          <SelectionBar.SaveButton onClick={onSave}>
            {t('action.save')}
          </SelectionBar.SaveButton>
        </div>
      </SelectionBar.Actions>

      <SelectionBar.Items>
        <SelectionBar.ReorderGroup
          items={selectedProducts}
          getItemId={(product) => product.id}
          onReorder={(reordered) => onReorder(reordered.map((p) => p.id))}
        >
          {(product) => (
            <SelectedItem
              product={product}
              onDelete={() => onProductRemove(product.id)}
            />
          )}
        </SelectionBar.ReorderGroup>

        <SelectionBar.Placeholder />
      </SelectionBar.Items>
    </SelectionBar>
  )
}

const SelectedItem = ({
  product,
  onDelete,
}: {
  product: SerializeFrom<ProductFull>
  onDelete?: () => void
}) => {
  const media = useMemo(() => getProductCoverMedia(product), [product])

  return (
    media && (
      <MediaCard
        format="square"
        className={cn(
          'xs:h-24 h-16',
          'ring-accent-element hover:ring-border/5 aspect-square cursor-grab ring-0 transition hover:ring-2'
        )}
      >
        <MediaCard.Cover>
          {media?.kind === 'image' ? (
            <Image
              src={media?.src}
              transformation={{
                width: 200,
                format: 'webp',
                resizeMode: 'fill',
              }}
              sizes={{ default: '150px' }}
              className={cn(
                'touch-callout-none touch-action-pan-y h-full w-full object-cover'
              )}
            />
          ) : (
            <Video
              src={media?.src}
              className={cn(
                'touch-callout-none touch-action-pan-y h-full w-full object-cover'
              )}
            />
          )}
        </MediaCard.Cover>
        {/* Delete button */}
        <MediaCard.CloseButton
          onClick={(e) => {
            e.stopPropagation()
            onDelete?.()
          }}
        />
      </MediaCard>
    )
  )
}

// Main component
export const CollectionItemsDrawer = ({
  existingItems,
  collection,
  isOpen,
  onOpenChange,
  onClose,
}: CollectionItemsDrawerProps) => {
  const { t } = useTranslation()
  const shop = useShop()
  const { dispatch } = useCollectionConfiguration()

  const getProductsFetcher = useFetcher<{
    products: SerializeFrom<ProductFull>[]
  }>()

  const [searchValue, setSearchValue] = useState('')
  const [itemsToUpdate, setItemsToUpdate] = useState<CollectionItemFormData[]>(
    existingItems || []
  )

  const [error, setError] = useState<boolean>(false)

  // Derived state
  const availableProducts = getProductsFetcher.data?.products || []
  const filteredProducts = useProductSearch(availableProducts, searchValue)
  const { selectedProducts, selectedProductsIds } =
    useSelectedProducts(itemsToUpdate)
  const isPanelLoading =
    getProductsFetcher?.state !== 'idle' && availableProducts.length === 0

  // Track if items have changed
  const hasUnsavedChanges = useMemo(() => {
    if (!existingItems || !itemsToUpdate) return false

    // Check length first
    if (existingItems.length !== itemsToUpdate.length) return true

    // Check if product IDs match in order
    return !existingItems.every(
      (item, index) => item.productId === itemsToUpdate[index]?.productId
    )
  }, [existingItems, itemsToUpdate])

  // Sync itemsToUpdate with existingItems when drawer opens or existingItems changes
  useEffect(() => {
    if (isOpen) {
      setItemsToUpdate(existingItems || [])
    }
  }, [isOpen, existingItems])

  // Load products when drawer opens
  useEffect(() => {
    if (
      isOpen &&
      !getProductsFetcher.data &&
      getProductsFetcher.state === 'idle'
    ) {
      getProductsFetcher.load(
        makeShopOwnPath(
          shop,
          `/collections/${collection.id}/list-available-products`
        )
      )
    }
  }, [isOpen, getProductsFetcher, shop, collection.id])

  // Event handlers
  const handleProductToggle = useCallback(
    (product: SerializeFrom<ProductFull>, checked: boolean) => {
      setItemsToUpdate((oldState) =>
        checked
          ? [
              ...(oldState ?? []),
              {
                id: undefined,
                collectionId: collection?.id, // TODO: confirm why we need collectionId
                productId: product.id,
                disposition: undefined,
                product: product,
              },
            ]
          : oldState?.filter((i) => i.productId !== product.id)
      )
    },
    [collection.id]
  )

  const handleProductRemove = useCallback((productId: string) => {
    setItemsToUpdate((oldState) =>
      oldState?.filter((i) => i.productId !== productId)
    )
  }, [])

  const handleReorder = useCallback((newOrder: string[]) => {
    setItemsToUpdate((oldState) => {
      const reordered = newOrder
        .map((id) => oldState.find((item) => item.productId === id)!)
        .filter(Boolean)
      return reordered
    })
  }, [])

  const onSave = useCallback(async () => {
    setError(false)
    const result = itemsToUpdate.map((item, index) => {
      return {
        id: item.id,
        productId: item.productId,
        disposition: itemsToUpdate.length - index,
      }
    })

    const collectionItemsFormData: CollectionItemFormData[] = result.map(
      (item) => ({
        id: item.id,
        collectionId: collection.id,
        productId: item.productId,
        product: itemsToUpdate.find((i) => i.productId === item.productId)
          ?.product as SerializeFrom<ProductFull>,
      })
    )

    dispatch({
      type: 'patch-collection-items',
      payload: collectionItemsFormData,
    })

    onClose()
  }, [itemsToUpdate, dispatch, onClose, collection.id])

  // Unsaved changes warning
  const { handleClose: handleCloseWithWarning, ConfirmationModal } =
    useUnsavedChangesWarning({
      hasUnsavedChanges,
      onSave,
      onDiscard: onClose,
    })

  return (
    <>
      <ConfirmationModal />

      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseWithWarning()
            return
          }
          onOpenChange(open)
        }}
      >
        <Drawer.Content
          direction="bottom"
          className="flex flex-col focus-visible:ring-0"
        >
          {/* Header */}
          <DrawerHeaderLayout className="grid grid-cols-[auto_1fr] gap-4 md:grid-cols-3">
            <Input.Group className="mr-auto">
              <Input.Prefix>
                <SearchIcon />
              </Input.Prefix>

              <Input
                placeholder={t(
                  'page.collections.save_collection_items.add_product_to_collection.search_placeholder'
                )}
                onChange={(e) => setSearchValue(e.target.value)}
                value={searchValue}
              />
            </Input.Group>

            <Drawer.Title className="text-foreground mx-auto hidden text-sm font-medium md:block">
              {`${collection.name ?? 'Your collection'} Products`}
            </Drawer.Title>

            <IconButton
              className="ml-auto"
              variant="ghost"
              size="md"
              aria-label={t('action.close')}
              onClick={handleCloseWithWarning}
            >
              <XIcon />
            </IconButton>
          </DrawerHeaderLayout>

          {isPanelLoading ? (
            <div className="relative flex h-2/3 w-full items-center justify-center overflow-y-hidden">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="flex flex-1 flex-col overflow-y-auto pt-4">
              <ProductGrid
                products={filteredProducts.filter((product) => {
                  return !selectedProductsIds.includes(product.id)
                })}
                selectedProductsIds={selectedProductsIds}
                onProductToggle={handleProductToggle}
              />

              <SelectedProductsBar
                selectedProducts={selectedProducts}
                onReorder={handleReorder}
                onProductRemove={handleProductRemove}
                onSave={onSave}
                error={error}
              />
            </div>
          )}
        </Drawer.Content>
      </Drawer>
    </>
  )
}
